import { useCallback, useEffect, useState } from 'react';
import type { AvailableEventView, EventFeeStatus, ParticipationStatus } from '@cuito/types';
import { Button } from '@cuito/ui';
import {
  PARTICIPATION_PAGE_SIZE,
  participationsApi,
  type MyParticipationItem,
} from '../api/participations';
import { readErrorCode, readServerMessage } from '../lib/apiError';
import { AMOUNT_UNAVAILABLE_TEXT, formatCents, formatServerYuan } from '../lib/schemeForm';

/**
 * 我报名的展会（`05-ui-design-system.md` §3.2 的 `/participations`）—— **两个 tab 两个端点**。
 *
 * | tab | 端点 | 内容 |
 * |---|---|---|
 * | `participations-tab-mine` | `GET /participations` | 报名记录：状态 + 待缴费入口（收款码）+ 上传缴费凭证 |
 * | `participations-tab-available` | `GET /events/available` | 可报名展会 + `participation-register` |
 *
 * ## 两个概念不许混用（README §3.2 / 契约 §11.4）
 *
 * 这一页里是**报名记录**（过程数据），不是**展会存档**（承载库存与账本的地点）。
 * 报名审核通过（`APPROVED`）**不会**在这里多出一个"展会"，只有 `PAID` 才会在
 * `/events` 里出现存档 —— 两个页面看的是两件事，任何一边"顺手合并"都会让产品口径失效。
 *
 * ## `paymentEntry.available` 由服务端算
 *
 * 只有 `status = APPROVED` 且 `feeStatus ∈ { UNPAID, REJECTED }` 时为 `true`。
 * 前端**不自己拼这个条件**（那是第二份状态机），也**不把金额算来算去**：
 * 摊位费一律用服务端读数（见 `amountText`）。
 *
 * ## 上传缴费凭证走**形态 B**（先传文件拿 URL，再提交 URL）
 *
 * 本页只收一个 URL（`participation-proof-url`）：形态 A 的 `multipart` 上传需要
 * 一个文件选择控件与 `POST /files/upload` 的两步流程，冻结 testid 清单里没有它们的位置。
 */

/** 报名状态的中文（契约 §11.4 的状态表逐字） */
const STATUS_LABELS: Record<ParticipationStatus, string> = {
  PENDING: '待审报名',
  REJECTED: '报名驳回',
  APPROVED: '待缴费',
  SUBMITTED: '待审缴费',
  PAID: '已缴费',
  CANCELLED: '已取消',
};

const FEE_STATUS_LABELS: Record<EventFeeStatus, string> = {
  UNPAID: '未缴费',
  SUBMITTED: '待审缴费',
  PAID: '已缴费',
  REJECTED: '凭证被驳回',
};

/**
 * 服务端金额 → 展示串；`null` / 读不出来落到 `金额以服务端为准`。
 *
 * ⚠️ 本仓服务端金额有**两条并存的口径**（`04-api-contract.md` §1.7 与 §11.4），
 * 两条都要走 `@cuito/ui` 的**同一份**实现，页面里零加减、零 `toFixed`：
 *   - **字符串** = 十进制元（报表的 `netAmount` / `totalRevenue` 那一类）⇒ `formatServerYuan`；
 *   - **数字** = **分位整数**（`paymentEntry.stallFeeCents`、`feeAmountCents`）⇒ `formatCents`
 *     （就是 `@cuito/ui` 的 `formatYuan`；`amountHonesty.spec.tsx` 钉住 `formatServerYuan(1280)
 *     === '¥1,280.00'`，即**它的数值入参按元解释**）。
 * 把分位整数交给 `formatServerYuan` 会渲染成 100 倍（80000 分 ⇒ `¥80,000.00`）——
 * `ParticipationsPage.spec.tsx` ② 的 `¥800.00` 与紧随其后的负向断言就是这条口径的判据。
 */
function amountText(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return AMOUNT_UNAVAILABLE_TEXT;
  if (typeof value === 'number') return formatCents(value);
  return formatServerYuan(value) ?? AMOUNT_UNAVAILABLE_TEXT;
}

type Tab = 'mine' | 'available';

function tabClass(selected: boolean): string {
  return `rounded-lg border-2 border-black px-4 py-2 text-sm font-bold ${
    selected ? 'bg-black text-white' : 'bg-white text-black'
  }`;
}

export default function ParticipationsPage() {
  const [tab, setTab] = useState<Tab>('mine');
  const [mine, setMine] = useState<MyParticipationItem[]>([]);
  const [available, setAvailable] = useState<AvailableEventView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [proofUrls, setProofUrls] = useState<Record<string, string>>({});
  const [contacts, setContacts] = useState<Record<string, { name: string; phone: string }>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'mine') {
        const res = await participationsApi.myList({ page: 1, pageSize: PARTICIPATION_PAGE_SIZE });
        setMine(res.items);
      } else {
        const res = await participationsApi.available({ page: 1, pageSize: PARTICIPATION_PAGE_SIZE });
        setAvailable(res.items);
      }
    } catch (e) {
      setError(readServerMessage(e) ?? '加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  async function submitProof(participationId: string): Promise<void> {
    const url = (proofUrls[participationId] ?? '').trim();
    setBusyId(participationId);
    setActionError(null);
    try {
      await participationsApi.submitPaymentProof(participationId, url);
      await load();
    } catch (e) {
      const code = readErrorCode(e);
      if (code === 'PARTICIPATION_NOT_PENDING') {
        // 规格原文写「该场次已确认缴费」；「场次」是 §7.7 的禁用同义词 ⇒ 逐字改为「展会」
        setActionError('该展会已确认缴费，无需再次上传');
      } else {
        setActionError(readServerMessage(e) ?? '上传失败，请重试');
      }
    } finally {
      setBusyId(null);
    }
  }

  async function register(eventId: string): Promise<void> {
    const contact = contacts[eventId] ?? { name: '', phone: '' };
    setBusyId(eventId);
    setActionError(null);
    try {
      await participationsApi.register(eventId, {
        contactName: contact.name.trim(),
        contactPhone: contact.phone.trim(),
      });
      setTab('mine');
    } catch (e) {
      setActionError(
        readErrorCode(e) === 'EVENT_ALREADY_REGISTERED'
          ? '已报名该展会'
          : (readServerMessage(e) ?? '保存失败，请重试'),
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="max-w-7xl" data-testid="participations-page">
      <h1 className="mb-6 text-2xl font-bold text-gray-900">我报名的展会</h1>

      <div className="mb-4 flex gap-2">
        <button
          type="button"
          data-testid="participations-tab-mine"
          aria-pressed={tab === 'mine'}
          onClick={() => setTab('mine')}
          className={tabClass(tab === 'mine')}
        >
          我报名的
        </button>
        <button
          type="button"
          data-testid="participations-tab-available"
          aria-pressed={tab === 'available'}
          onClick={() => setTab('available')}
          className={tabClass(tab === 'available')}
        >
          可报名展会
        </button>
      </div>

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {!loading && error !== null && <p className="font-bold text-red-600">{error}</p>}
      {actionError !== null && <p className="mb-3 font-bold text-red-600">{actionError}</p>}

      {!loading && error === null && tab === 'mine' && mine.length === 0 && (
        <p className="text-gray-500">暂无报名记录</p>
      )}
      {!loading && error === null && tab === 'available' && available.length === 0 && (
        <p className="text-gray-500">暂无可报名展会</p>
      )}

      {!loading && error === null && tab === 'mine' && mine.length > 0 && (
        <table className="w-full rounded-lg border-2 border-black">
          <thead className="bg-gray-50">
            <tr>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">展会</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">主办方</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">场馆</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">日期</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">报名状态</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">缴费状态</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">缴费</th>
            </tr>
          </thead>
          <tbody>
            {mine.map((row) => (
              <tr
                key={row.participationId}
                data-testid="participation-row"
                data-participation-id={row.participationId}
                data-status={row.status}
              >
                <td className="border-b border-gray-200 px-4 py-3 text-sm font-bold">
                  {row.event.name}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {row.event.organizer.name}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">{row.event.venue}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {row.event.dates.join('、')}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  <span data-testid="participation-status">{STATUS_LABELS[row.status]}</span>
                  {row.rejectReason !== null && (
                    <span className="ml-2 text-xs text-gray-600">{row.rejectReason}</span>
                  )}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  <span data-testid="participation-fee-status">
                    {FEE_STATUS_LABELS[row.feeStatus]}
                  </span>
                  {row.paymentRejectReason !== null && (
                    <span className="ml-2 text-xs text-red-600">
                      凭证被驳回：{row.paymentRejectReason}，请重新上传
                    </span>
                  )}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {row.paymentEntry.available && (
                    <div className="flex flex-col gap-2" data-testid="participation-payment-entry">
                      <p className="text-sm font-bold">
                        摊位费 {amountText(row.paymentEntry.stallFeeCents)}
                      </p>
                      <div className="flex gap-2">
                        {row.paymentEntry.feeWechatQrUrl !== null && (
                          <img
                            src={row.paymentEntry.feeWechatQrUrl}
                            alt="微信收款码"
                            className="h-[33vh] w-[33vh] object-contain"
                          />
                        )}
                        {row.paymentEntry.feeAlipayQrUrl !== null && (
                          <img
                            src={row.paymentEntry.feeAlipayQrUrl}
                            alt="支付宝收款码"
                            className="h-[33vh] w-[33vh] object-contain"
                          />
                        )}
                      </div>
                      {row.paymentEntry.feeNote !== null && (
                        <p className="text-xs text-gray-600">{row.paymentEntry.feeNote}</p>
                      )}
                      <input
                        data-testid="participation-proof-url"
                        value={proofUrls[row.participationId] ?? row.paymentProofUrl ?? ''}
                        onChange={(e) =>
                          setProofUrls((current) => ({
                            ...current,
                            [row.participationId]: e.target.value,
                          }))
                        }
                        placeholder="缴费凭证地址"
                        className="h-10 rounded-lg border-2 border-black bg-white px-2 text-sm outline-none focus:ring-2 focus:ring-black"
                      />
                      <Button
                        size="sm"
                        data-testid="participation-submit-proof"
                        disabled={busyId === row.participationId}
                        onClick={() => void submitProof(row.participationId)}
                      >
                        上传缴费凭证
                      </Button>
                    </div>
                  )}
                  {!row.paymentEntry.available && row.archiveLocationId !== null && (
                    <span className="text-xs text-gray-600">展会存档已创建</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && error === null && tab === 'available' && available.length > 0 && (
        <table className="w-full rounded-lg border-2 border-black">
          <thead className="bg-gray-50">
            <tr>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">展会</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">主办方</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">场馆</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">日期</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">报名</th>
            </tr>
          </thead>
          <tbody>
            {available.map((row) => {
              const contact = contacts[row.eventId] ?? { name: '', phone: '' };
              const ready = contact.name.trim() !== '' && contact.phone.trim() !== '';
              return (
                <tr
                  key={row.eventId}
                  data-testid="participation-row"
                  data-event-id={row.eventId}
                  data-registered={row.myParticipation !== null}
                >
                  <td className="border-b border-gray-200 px-4 py-3 text-sm font-bold">{row.name}</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-sm">{row.organizer.name}</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-sm">{row.venue}</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-sm">{row.dates.join('、')}</td>
                  <td className="border-b border-gray-200 px-4 py-3 text-sm">
                    {row.myParticipation === null ? (
                      <div className="flex flex-col gap-2">
                        <input
                          data-field="contact-name"
                          value={contact.name}
                          onChange={(e) =>
                            setContacts((current) => ({
                              ...current,
                              [row.eventId]: { ...contact, name: e.target.value },
                            }))
                          }
                          placeholder="联系人"
                          className="h-10 rounded-lg border-2 border-black bg-white px-2 text-sm outline-none focus:ring-2 focus:ring-black"
                        />
                        <input
                          data-field="contact-phone"
                          value={contact.phone}
                          onChange={(e) =>
                            setContacts((current) => ({
                              ...current,
                              [row.eventId]: { ...contact, phone: e.target.value },
                            }))
                          }
                          placeholder="联系电话"
                          className="h-10 rounded-lg border-2 border-black bg-white px-2 text-sm outline-none focus:ring-2 focus:ring-black"
                        />
                        <Button
                          size="sm"
                          data-testid="participation-register"
                          disabled={!ready || busyId === row.eventId}
                          onClick={() => void register(row.eventId)}
                        >
                          报名
                        </Button>
                      </div>
                    ) : (
                      <span>{STATUS_LABELS[row.myParticipation.status]}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
